import { FunctionComponent, useEffect, useState } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Box, Dialog, DialogTitle, DialogContent, Typography, useMediaQuery } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import ProductCategory from 'model/ProductCategory';
import { Button, Checkbox, Spinner } from 'components/UI';
import {
    deleteProductsThunk,
    fetchThunk as fetchCategoriesThunk,
    saveProductsThunk
} from 'actions/productCategories/ProductCategoriesActions';
import { ProductCategoriesStatus } from 'reducers/productCategories/ProductCategoriesState';
import { RootState } from 'store';
import AssignProductsHeader from './AssignProductsHeader';
import { SelectableOption } from 'model';
import { Product } from '@spike/product-model';
import { Option } from '@spike/model';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { fetchProductsThunk } from '@spike/products-action';
import { fetchThunk as fetchVendorsThunk } from 'actions/productVendors/ProductVendorsActions';
import { ProductsState } from '@spike/products-action';
import { useApiClientWrapper } from 'hooks';

interface Props {
    category: ProductCategory;
    products: Array<Product>;
    onClose: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        titleContainer: {
            'backgroundColor': '#E9F0ED',
            'flexShrink': 0,
            [theme.breakpoints.down(768)]: {
                height: 'auto',
                minHeight: '44px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                height: '37px'
            },
            [theme.breakpoints.up('lg')]: {
                height: '46px'
            },
            ' & .MuiTypography-h6': {
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between'
            },
            '&.MuiDialogTitle-root': {
                [theme.breakpoints.down(768)]: {
                    padding: '12px 16px'
                },
                [theme.breakpoints.between(768, 'md')]: {
                    paddingLeft: '43px',
                    paddingTop: '11px'
                },
                [theme.breakpoints.up('lg')]: {
                    paddingLeft: '43px',
                    paddingTop: '11px'
                }
            }
        },
        title: {
            fontWeight: 600,
            color: '#5E8677',
            letterSpacing: 'normal',
            [theme.breakpoints.down(768)]: {
                fontSize: '14px',
                lineHeight: '20px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                fontSize: '13px',
                lineHeight: '19px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '16px',
                lineHeight: '24px'
            }
        },
        container: {
            '& .MuiDialog-paper': {
                boxShadow: '0px 10px 40px #00000029',
                [theme.breakpoints.down(768)]: {
                    width: '100%',
                    minWidth: '100%',
                    maxWidth: '100%',
                    height: '100%',
                    maxHeight: '100%',
                    margin: 0,
                    borderRadius: 0,
                    display: 'flex',
                    flexDirection: 'column'
                },
                [theme.breakpoints.between(768, 'md')]: {
                    width: '582px',
                    minWidth: '582px',
                    height: '614px',
                    borderRadius: '19px'
                },
                [theme.breakpoints.up('lg')]: {
                    width: '728px',
                    minWidth: '728px',
                    height: '768px',
                    borderRadius: '24px'
                }
            },
            '& .MuiDialogContent-root': {
                overflowY: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                [theme.breakpoints.down(768)]: {
                    padding: '16px'
                },
                [theme.breakpoints.between(768, 'md')]: {
                    padding: '35px'
                },
                [theme.breakpoints.up('lg')]: {
                    padding: '44px'
                }
            }
        },
        dialogContainer: {
            'width': '100%',
            'height': '100%',
            'minHeight': 0,
            'display': 'flex',
            'flexDirection': 'column',
            '& .MuiDialog-paper': {
                overflowY: 'hidden'
            }
        },
        buttonsContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
            gap: '12px',
            [theme.breakpoints.down(768)]: {
                flexDirection: 'column',
                alignItems: 'stretch',
                marginTop: '16px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                marginTop: '42px'
            },
            [theme.breakpoints.up('lg')]: {
                marginTop: '52px'
            }
        },
        button: {
            [theme.breakpoints.down(768)]: {
                'width': '100%',
                'height': '44px',
                '& .MuiButton-label': {
                    fontSize: '15px'
                }
            },
            [theme.breakpoints.between(768, 'md')]: {
                'width': '136px',
                'height': '43px',
                '& .MuiButton-label': {
                    fontSize: '15px'
                }
            },
            [theme.breakpoints.up('lg')]: {
                'width': '170px',
                'height': '54px',
                '& .MuiButton-label': {
                    fontSize: '18px'
                }
            }
        },
        productsContainer: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            [theme.breakpoints.down(768)]: {
                height: 'auto'
            },
            [theme.breakpoints.between(768, 'md')]: {
                height: '320px'
            },
            [theme.breakpoints.up('lg')]: {
                height: '400px'
            }
        },
        check: {
            'marginTop': '3px',
            'flexShrink': 0,
            [theme.breakpoints.down(768)]: {
                marginBottom: '12px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                marginBottom: '19px'
            },
            [theme.breakpoints.up('lg')]: {
                marginBottom: '24px'
            },
            '& .MuiSvgIcon-root': {
                [theme.breakpoints.down('md')]: {
                    fontSize: '19px'
                },
                [theme.breakpoints.up('lg')]: {
                    fontSize: '24px'
                }
            }
        },
        label: {
            minWidth: 0,
            wordBreak: 'break-word',
            [theme.breakpoints.down(768)]: {
                fontSize: '14px',
                lineHeight: '20px',
                paddingBottom: '12px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                fontSize: '15px',
                paddingBottom: '19px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '18px',
                paddingBottom: '24px'
            }
        },
        checked: {
            fontWeight: 600
        },
        checkboxContainer: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            minWidth: 0
        },
        productsScrollContainer: {
            flex: 1,
            minHeight: 0,
            [theme.breakpoints.down(768)]: {
                height: 'auto',
                maxHeight: '100%'
            },
            [theme.breakpoints.between(768, 'md')]: {
                height: '320px'
            },
            [theme.breakpoints.up('lg')]: {
                height: '400px'
            }
        },
        iconTimes: {
            'fontWeight': 400,
            '&:hover': {
                cursor: 'pointer'
            },
            [theme.breakpoints.down('md')]: {
                fontSize: '19px',
                lineHeight: '19px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '24px',
                lineHeight: '24px'
            }
        },
        clearAll: {
            fontWeight: 600,
            cursor: 'pointer',
            color: '#BCB8AE',
            [theme.breakpoints.down(768)]: {
                fontSize: '14px',
                lineHeight: '18px',
                marginRight: 0,
                marginTop: 0,
                textAlign: 'center'
            },
            [theme.breakpoints.between(768, 'md')]: {
                fontSize: '15px',
                lineHeight: '19px',
                marginRight: '34px',
                marginTop: '6px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '18px',
                lineHeight: '24px',
                marginRight: '30px',
                marginTop: '8px'
            }
        },
        emptyProductsContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
        },
        emptyProducts: {
            fontWeight: 600,
            textAlign: 'center',
            [theme.breakpoints.down(768)]: {
                fontSize: '16px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                fontSize: '18px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '22px'
            }
        },
        marginButton: {
            [theme.breakpoints.down(768)]: {
                marginLeft: 0
            },
            [theme.breakpoints.between(768, 'md')]: {
                marginLeft: '10px'
            },
            [theme.breakpoints.up('lg')]: {
                marginLeft: '12px'
            }
        },
        buttons: {
            display: 'flex',
            [theme.breakpoints.down(768)]: {
                width: '100%',
                justifyContent: 'center'
            }
        },
        actionButtons: {
            display: 'flex',
            [theme.breakpoints.down(768)]: {
                width: '100%',
                flexDirection: 'column-reverse',
                gap: '12px'
            }
        }
    })
);

export const AssignProductsComponent: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const isMobile = useMediaQuery('(max-width:767.95px)');

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const products = useSelector<RootState, ProductsState>(state => state.products);
    const status = useSelector<RootState, ProductCategoriesStatus>(state => state.productCategories.status);
    const loading = useSelector<RootState, boolean>(state => state.productCategories.loading);
    const productsLoading = useSelector<RootState, boolean>(state => state.products.loading);

    const [category, setCategory] = useState<ProductCategory>(props.category);
    const [productsSelectable, setProductsSelectable] = useState<Array<SelectableOption<number>>>([]);
    const [productsOriginal, setProductsOriginal] = useState<Array<SelectableOption<number>>>([]);
    const [productNumber, setProductNumber] = useState(productsSelectable.length);
    const [loadingProducts, setLoadingProducts] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const loadProducts = async () => {
            setLoadingProducts(true);
            await dispatch(
                fetchProductsThunk(apiClientWrapper, '', [], [apiClientWrapper.marketplaceId], [], 1, 10000)
            );
            if (!cancelled) {
                setLoadingProducts(false);
            }
        };

        loadProducts();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (
            status === ProductCategoriesStatus.SaveProductsSuccess ||
            status === ProductCategoriesStatus.DeleteProductsSuccess
        ) {
            dispatch(fetchProductsThunk(apiClientWrapper, '', [], [apiClientWrapper.marketplaceId], [], 1, 10000));
            dispatch(fetchVendorsThunk());
            dispatch(fetchCategoriesThunk());
            props.onClose();
        }
    }, [status]);

    useEffect(() => {
        const productsAux: Array<SelectableOption<number>> = [];

        if (products) {
            let productCount = 0;
            products.list.page.forEach(product => {
                const productFounded = props.category.products.find(prod => prod.id === product.id) ? true : false;
                productsAux.push({
                    element: {
                        id: product.id!,
                        name: product.businessProduct?.name ? product.businessProduct.name : product.name,
                        description: product.description
                    },
                    selected: productFounded
                });
                if (productFounded) {
                    productCount++;
                }
            });
            setProductNumber(productCount);
            setProductsSelectable(productsAux);
            setProductsOriginal(productsAux);
        }
    }, [props.products]);

    const saveCategoryHandler = () => {
        const productsToAdd: Array<Product> = [];
        const productsToDelete: Array<Product> = [];
        const categoryAddAux = { ...category };
        const categoryDeleteAux = { ...category };

        category.products.forEach(p => {
            const categoryFounded = props.category.products.find(pc => pc.id === p.id);
            const productFounded = products.list.page.find(prod => prod.id === p.id);
            if (!categoryFounded && productFounded) {
                productsToAdd.push(p);
            }
        });

        if (productsToAdd.length > 0) {
            categoryAddAux.products = productsToAdd;
            dispatch(saveProductsThunk(categoryAddAux));
        }

        props.category.products.forEach(p => {
            const categoryFounded = category.products.find(pc => pc.id === p.id);
            const productFounded = products.list.page.find(prod => prod.id === p.id);
            if (!categoryFounded && productFounded) {
                productsToDelete.push(p);
            }
        });

        if (productsToDelete.length > 0) {
            categoryDeleteAux.products = productsToDelete;
            dispatch(deleteProductsThunk(categoryDeleteAux));
        }
    };

    const handleChange = (value: Option<number>, selected: boolean) => {
        const newSelectableProducts = [...productsSelectable];
        const newProducts: Array<Product> = [];
        let productCount = 0;

        newSelectableProducts.forEach(prod => {
            if (prod.element.id === value.id) {
                prod.selected = selected;
            }
            if (prod.selected) {
                const productAux = props.products.find(p => p.id === prod.element.id);
                if (productAux) {
                    newProducts.push(productAux);
                }
                productCount++;
            }
        });

        setProductsSelectable(newSelectableProducts);
        setProductNumber(productCount);
        setCategory((prevCategory: any) => {
            return {
                ...prevCategory,
                products: newProducts
            };
        });
    };

    const handleSearchProducts = (searchText: string) => {
        let productListFiltered: Array<SelectableOption<number>> = [];

        if (searchText) {
            const prodcutListAux = productsSelectable.filter(item => {
                const name = `${item.element.name.toLowerCase()}`;
                const barcode = item.element.description ? `${item.element.description.toLowerCase()}` : '';
                return (
                    searchText.length === 0 ||
                    name.indexOf(searchText.toLowerCase()) > -1 ||
                    barcode.indexOf(searchText.toLowerCase()) > -1
                );
            });
            productListFiltered = prodcutListAux;
        } else {
            productListFiltered = productsOriginal;
        }
        setProductsSelectable(productListFiltered);
    };

    const handleClearAll = () => {
        const newSelectableProducts = [...productsSelectable];
        const newProducts: Array<Product> = [];
        const productCount = 0;

        newSelectableProducts.forEach(prod => {
            prod.selected = false;
        });

        setProductsSelectable(newSelectableProducts);
        setProductNumber(productCount);
        setCategory((prevCategory: any) => {
            return {
                ...prevCategory,
                products: newProducts
            };
        });
    };

    return (
        <Box>
            <Dialog
                open={true}
                fullScreen={isMobile}
                className={clsx(classes.container)}
            >
                <DialogTitle className={classes.titleContainer}>
                    <Typography className={classes.title}>Assign products</Typography>
                </DialogTitle>
                <DialogContent className={classes.dialogContainer}>
                    <AssignProductsHeader
                        category={category!}
                        productNumber={productNumber}
                        onSearch={handleSearchProducts}
                    />
                    {loadingProducts || productsLoading ? (
                        <Box className={clsx(classes.productsScrollContainer, classes.emptyProductsContainer)}>
                            <Spinner />
                        </Box>
                    ) : productsSelectable.length > 0 ? (
                        <OverlayScrollbarsComponent className={classes.productsScrollContainer}>
                            <Box className={classes.productsContainer}>
                                {productsSelectable.map(product => {
                                    return (
                                        <Box
                                            className={classes.checkboxContainer}
                                            key={product.element.id}
                                        >
                                            <Checkbox
                                                label=""
                                                checked={product.selected}
                                                onChange={() => handleChange(product.element, !product.selected)}
                                                className={clsx(classes.check, {
                                                    [classes.checked]: product.selected
                                                })}
                                            />
                                            <Typography
                                                className={clsx(classes.label, {
                                                    [classes.checked]: product.selected
                                                })}
                                            >
                                                {product.element.name}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </OverlayScrollbarsComponent>
                    ) : (
                        <Box className={clsx(classes.productsScrollContainer, classes.emptyProductsContainer)}>
                            <Typography className={classes.emptyProducts}>
                                There are no products to be assigned.
                            </Typography>
                        </Box>
                    )}
                    <Box className={classes.buttonsContainer}>
                        <Box className={classes.buttons}>
                            <Typography
                                onClick={handleClearAll}
                                className={classes.clearAll}
                            >
                                clear all
                            </Typography>
                        </Box>
                        <Box className={classes.actionButtons}>
                            <Button
                                label="Cancel"
                                onClick={() => props.onClose()}
                                variant="secondary"
                                size="medium"
                                color="orange"
                                className={classes.button}
                            />
                            {productsSelectable.length > 0 && (
                                <Button
                                    label="Save"
                                    loading={loading}
                                    onClick={saveCategoryHandler}
                                    variant="primary"
                                    size="medium"
                                    color="orange"
                                    className={clsx(classes.button, classes.marginButton)}
                                />
                            )}
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default AssignProductsComponent;
